// src/errors.ts
var IllegalMoveError = class extends Error {
  name = "IllegalMoveError";
  code;
  constructor(code, message) {
    super(message ?? code);
    this.code = code;
  }
};
function illegal(code, message) {
  throw new IllegalMoveError(code, message);
}

// src/invariant.ts
var EngineInvariantError = class extends Error {
  name = "EngineInvariantError";
};
function must(value, message = "engine invariant violated") {
  if (value === void 0 || value === null) {
    throw new EngineInvariantError(message);
  }
  return value;
}
function invariant(condition, message = "engine invariant violated") {
  if (!condition) {
    throw new EngineInvariantError(message);
  }
}

// src/internal.ts
function emit(ctx, event) {
  const seq = ctx.draft.seq;
  ctx.draft.seq = seq + 1;
  ctx.events.push({ ...event, seq });
}
function emptyStats() {
  return {
    hands_played: 0,
    total_points: 0,
    pegging_points: 0,
    hand_points: 0,
    crib_points: 0,
    grid_points: 0,
    highest_hand: 0,
    best_grid_line: 0,
    twenty_nine_hands: 0
  };
}
function draftOf(state) {
  return {
    config: state.config,
    engineVersion: state.engineVersion,
    rng: state.rng,
    seq: state.seq,
    phase: state.phase,
    handNumber: state.handNumber,
    dealer: state.dealer,
    scores: [...state.scores],
    prevScores: [...state.prevScores],
    deck: [...state.deck],
    hands: state.hands.map((h) => [...h]),
    showHands: state.showHands.map((h) => [...h]),
    crib: [...state.crib],
    discards: state.discards.map((d) => [...d]),
    starter: state.starter,
    discarded: [...state.discarded],
    pegging: clonePegging(state.pegging),
    cross: cloneCross(state.cross),
    roleDraw: copyRoleDraw(state.roleDraw),
    stats: state.stats.map((s) => ({ ...s })),
    winner: state.winner,
    result: state.result
  };
}
function clonePegging(pegging) {
  if (pegging === null) return null;
  return {
    count: pegging.count,
    stack: [...pegging.stack],
    history: pegging.history.map((p) => ({ ...p })),
    turn: pegging.turn,
    saidGo: [...pegging.saidGo],
    lastPlayer: pegging.lastPlayer
  };
}
function cloneCross(cross) {
  if (cross === null) return null;
  return {
    board: [...cross.board],
    turn: cross.turn,
    drawn: cross.drawn,
    gridNumber: cross.gridNumber,
    throws: [...cross.throws]
  };
}
function copyRoleDraw(draw) {
  return draw == null ? null : draw.map((cut) => ({ ...cut }));
}
function commit(draft) {
  return draft;
}
function nextSeat(config, seat) {
  return (seat + 1) % config.playerCount;
}
function seatOrderFrom(config, first) {
  return Array.from({ length: config.playerCount }, (_, i) => (first + i) % config.playerCount);
}
function isPartners(config) {
  return config.playerCount === 4 && config.teams;
}
function isTeamAgainstOne(config) {
  return config.variant === "cross" && config.playerCount === 3;
}
var ALONE_SEAT = 1;
function teamOf(config, seat) {
  if (isPartners(config)) return seat % 2;
  if (isTeamAgainstOne(config)) return seat === ALONE_SEAT ? 1 : 0;
  return seat;
}
function teamCount(config) {
  return isPartners(config) || isTeamAgainstOne(config) ? 2 : config.playerCount;
}
function turnCycle(config) {
  return isTeamAgainstOne(config) ? TEAM_AGAINST_ONE_CYCLE : seatOrderFrom(config, 0);
}
var TEAM_AGAINST_ONE_CYCLE = [0, ALONE_SEAT, 2, ALONE_SEAT];
function dealDrawers(config) {
  return isTeamAgainstOne(config) ? [0, ALONE_SEAT] : seatOrderFrom(config, 0);
}
function dealerPlace(config, dealer, handNumber) {
  if (!isTeamAgainstOne(config) || dealer !== ALONE_SEAT) return turnCycle(config).indexOf(dealer);
  const deal = Math.ceil(handNumber / 2);
  return deal % 2 === 1 ? 1 : 3;
}
function seatsOfTeam(config, team) {
  return Array.from({ length: config.playerCount }, (_, seat) => seat).filter(
    (seat) => teamOf(config, seat) === team
  );
}
function cardsPerPlayer(config) {
  return config.playerCount === 2 ? 6 : 5;
}
function discardCount(config) {
  return config.playerCount === 2 ? 2 : 1;
}
var STAT_BY_SOURCE = {
  pegging: "pegging_points",
  hand: "hand_points",
  crib: "crib_points",
  heels: "pegging_points",
  grid: "grid_points"
};
function award(ctx, seat, items, source, credit = "seat") {
  const d = ctx.draft;
  const team = teamOf(d.config, seat);
  const credited = credit === "seat" ? [must(d.stats[seat])] : seatsOfTeam(d.config, team).map((member) => must(d.stats[member]));
  for (const item of items) {
    if (d.winner !== null) return;
    if (item.points <= 0) continue;
    const before = must(d.scores[team]);
    d.prevScores[team] = before;
    const raw = before + item.points;
    const total = d.config.variant === "cross" ? raw : Math.min(raw, d.config.targetScore);
    d.scores[team] = total;
    const pegged = total - before;
    for (const stats of credited) {
      stats.total_points += pegged;
      stats[STAT_BY_SOURCE[source]] += pegged;
    }
    emit(ctx, { type: "POINTS_SCORED", seat, team, item, total, source });
    if (total >= d.config.targetScore) {
      d.winner = team;
    }
  }
}
function skunkLine(targetScore) {
  return Math.ceil(targetScore * 3 / 4);
}
function doubleSkunkLine(targetScore) {
  return Math.ceil(targetScore / 2);
}
var CROSS_SKUNK_LINE = 16;
var CROSS_DOUBLE_SKUNK_LINE = 1;
function finalize(ctx, forfeitedBy = null) {
  const d = ctx.draft;
  if (d.result !== null) return;
  const forfeit = forfeitedBy !== null;
  const forfeitingTeam = forfeitedBy === null ? null : teamOf(d.config, forfeitedBy);
  const teams = Array.from({ length: teamCount(d.config) }, (_, team) => team);
  const winner = d.winner ?? teams.reduce((best, team) => must(d.scores[team]) > must(d.scores[best]) ? team : best, 0);
  const left = (team) => forfeitingTeam !== null && team === forfeitingTeam ? 1 : 0;
  const won = (team) => team === winner ? 1 : 0;
  const ranked = [...teams].sort(
    (a, b) => won(b) - won(a) || left(a) - left(b) || must(d.scores[b]) - must(d.scores[a])
  );
  const placementByTeam = /* @__PURE__ */ new Map();
  ranked.forEach((team, index) => {
    const prev = index > 0 ? must(ranked[index - 1]) : null;
    const placement = prev !== null && prev !== winner && left(prev) === left(team) && must(d.scores[prev]) === must(d.scores[team]) ? must(placementByTeam.get(prev)) : index + 1;
    placementByTeam.set(team, placement);
  });
  const cross = d.config.variant === "cross";
  const skunk = cross ? CROSS_SKUNK_LINE : skunkLine(d.config.targetScore);
  const doubleSkunk = cross ? CROSS_DOUBLE_SKUNK_LINE : doubleSkunkLine(d.config.targetScore);
  const seats = Array.from({ length: d.config.playerCount }, (_, seat) => {
    const team = teamOf(d.config, seat);
    const score = must(d.scores[team]);
    const won2 = team === winner;
    const skunked = !forfeit && !won2 && score < skunk;
    const doubleSkunked = !forfeit && !won2 && score < doubleSkunk;
    const seatForfeited = forfeitingTeam !== null && team === forfeitingTeam;
    const base = must(d.stats[seat]);
    const anySkunk = !forfeit && teams.some((t) => t !== winner && must(d.scores[t]) < skunk);
    const anyDoubleSkunk = !forfeit && teams.some((t) => t !== winner && must(d.scores[t]) < doubleSkunk);
    return {
      seat,
      team,
      score,
      placement: must(placementByTeam.get(team)),
      won: won2,
      skunked,
      doubleSkunked,
      forfeited: seatForfeited,
      stats: {
        ...base,
        games_played: 1,
        games_won: won2 ? 1 : 0,
        // Always present, so Rails never has to tell "no forfeit" from "old
        // engine" when it folds a result into a player's totals.
        games_forfeited: seatForfeited ? 1 : 0,
        skunks_for: won2 && anySkunk ? 1 : 0,
        skunks_against: skunked ? 1 : 0,
        double_skunks_for: won2 && anyDoubleSkunk ? 1 : 0,
        double_skunks_against: doubleSkunked ? 1 : 0
      }
    };
  });
  const result = {
    winner,
    scores: [...d.scores],
    seats,
    handsPlayed: d.handNumber,
    outcome: forfeit ? "forfeited" : "completed",
    forfeitedBy
  };
  d.result = result;
  d.winner = winner;
  d.phase = "gameOver";
  emit(ctx, { type: "PHASE_CHANGED", phase: "gameOver" });
  emit(ctx, { type: "GAME_ENDED", result });
}
function statsFor(ctx, seat) {
  return must(ctx.draft.stats[seat]);
}

// src/cards.ts
var SUIT_COUNT = 4;
var RANK_COUNT = 13;
var DECK_SIZE = SUIT_COUNT * RANK_COUNT;
var SUIT_SYMBOLS = ["\u2663", "\u2666", "\u2665", "\u2660"];
var SUIT_LETTERS = ["C", "D", "H", "S"];
var RANK_LETTERS = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "T", "J", "Q", "K"];
var RANK_PIPS = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];
var JACK = 11;
function suitOf(card) {
  return Math.floor(card / RANK_COUNT);
}
function rankOf(card) {
  return card % RANK_COUNT + 1;
}
function valueOf(card) {
  const rank = rankOf(card);
  return rank > 10 ? 10 : rank;
}
function makeCard(suit, rank) {
  invariant(rank >= 1 && rank <= RANK_COUNT, `bad rank ${rank}`);
  return suit * RANK_COUNT + (rank - 1);
}
function isCard(value) {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value < DECK_SIZE;
}
var FULL_DECK = Object.freeze(
  Array.from({ length: DECK_SIZE }, (_, i) => i)
);
function totalValue(cards) {
  let sum = 0;
  for (const card of cards) sum += valueOf(card);
  return sum;
}
function cardCode(card) {
  return `${must(RANK_LETTERS[rankOf(card) - 1])}${must(SUIT_LETTERS[suitOf(card)])}`;
}
function cardLabel(card) {
  return `${must(RANK_PIPS[rankOf(card) - 1])}${must(SUIT_SYMBOLS[suitOf(card)])}`;
}
function parseCard(code) {
  const trimmed = code.trim().toUpperCase();
  invariant(trimmed.length === 2, `bad card code "${code}"`);
  const rankIndex = RANK_LETTERS.indexOf(trimmed[0]);
  const suitIndex = SUIT_LETTERS.indexOf(trimmed[1]);
  invariant(rankIndex >= 0, `bad rank in "${code}"`);
  invariant(suitIndex >= 0, `bad suit in "${code}"`);
  return makeCard(suitIndex, rankIndex + 1);
}
function hand(...codes) {
  return codes.map(parseCard);
}
function formatCards(cards) {
  return cards.map(cardLabel).join(" ");
}
function compareCards(a, b) {
  return rankOf(a) - rankOf(b) || suitOf(a) - suitOf(b);
}
function sortCards(cards) {
  return [...cards].sort(compareCards);
}
function combinations(items, minSize) {
  const out = [];
  const n = items.length;
  invariant(n <= 20, "combinations() is only meant for small sets");
  for (let mask = 1; mask < 1 << n; mask++) {
    let size = 0;
    for (let bit = mask; bit; bit &= bit - 1) size++;
    if (size < minSize) continue;
    const subset = [];
    for (let i = 0; i < n; i++) {
      if (mask & 1 << i) subset.push(must(items[i]));
    }
    out.push(subset);
  }
  return out;
}
function choose(items, k) {
  const out = [];
  const n = items.length;
  if (k > n || k < 0) return out;
  const idx = Array.from({ length: k }, (_, i) => i);
  for (; ; ) {
    out.push(idx.map((i2) => must(items[i2])));
    let i = k - 1;
    while (i >= 0 && must(idx[i]) === n - k + i) i--;
    if (i < 0) break;
    idx[i] = must(idx[i]) + 1;
    for (let j = i + 1; j < k; j++) idx[j] = must(idx[j - 1]) + 1;
  }
  return out;
}

// src/rng.ts
function splitmix32(input) {
  let a = input + 2654435769 | 0;
  a = Math.imul(a ^ a >>> 16, 569420461);
  a = Math.imul(a ^ a >>> 15, 1935289751);
  return (a ^ a >>> 15) >>> 0;
}
function hashSeed(seed) {
  if (typeof seed === "number") return seed >>> 0;
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function seedRng(seed) {
  let x = hashSeed(seed);
  const s = [];
  for (let i = 0; i < 4; i++) {
    x = splitmix32(x);
    s.push(x);
  }
  const state = [must(s[0]), must(s[1]), must(s[2]), must(s[3])];
  if (state[0] === 0 && state[1] === 0 && state[2] === 0 && state[3] === 0) {
    return [1, 2, 3, 4];
  }
  return state;
}
function rotl(x, k) {
  return (x << k | x >>> 32 - k) >>> 0;
}
function nextUint32(state) {
  const [s0, s1, s2, s3] = state;
  const value = Math.imul(rotl(Math.imul(s1, 5) >>> 0, 7), 9) >>> 0 >>> 0;
  const t = s1 << 9 >>> 0;
  let n2 = (s2 ^ s0) >>> 0;
  let n3 = (s3 ^ s1) >>> 0;
  const n1 = (s1 ^ n2) >>> 0;
  const n0 = (s0 ^ n3) >>> 0;
  n2 = (n2 ^ t) >>> 0;
  n3 = rotl(n3, 11);
  return { state: [n0, n1, n2, n3], value };
}
function nextInt(state, bound) {
  invariant(bound > 0 && Number.isInteger(bound), "nextInt bound must be a positive integer");
  const limit = 4294967296 - 4294967296 % bound;
  let current = state;
  for (; ; ) {
    const draw = nextUint32(current);
    current = draw.state;
    if (draw.value < limit) {
      return { state: current, value: draw.value % bound };
    }
  }
}
function nextFloat(state) {
  const draw = nextUint32(state);
  return { state: draw.state, value: draw.value / 4294967296 };
}
function shuffle(items, state) {
  const out = [...items];
  let current = state;
  for (let i = out.length - 1; i > 0; i--) {
    const draw = nextInt(current, i + 1);
    current = draw.state;
    const j = draw.value;
    const a = must(out[i]);
    out[i] = must(out[j]);
    out[j] = a;
  }
  return { state: current, items: out };
}
function pick(items, state) {
  const draw = nextInt(state, items.length);
  return { state: draw.state, value: must(items[draw.value]) };
}

// src/scoring.ts
function totalPoints(items) {
  let sum = 0;
  for (const item of items) sum += item.points;
  return sum;
}
function scoreFifteens(cards) {
  const items = [];
  for (const subset of combinations(sortCards(cards), 2)) {
    if (totalValue(subset) === 15) {
      items.push({ reason: "fifteen", points: 2, cards: subset });
    }
  }
  return items;
}
function scorePairs(cards) {
  const byRank = /* @__PURE__ */ new Map();
  for (const card of sortCards(cards)) {
    const rank = rankOf(card);
    const group = byRank.get(rank);
    if (group) group.push(card);
    else byRank.set(rank, [card]);
  }
  const items = [];
  for (const rank of [...byRank.keys()].sort((a, b) => a - b)) {
    const group = must(byRank.get(rank));
    if (group.length >= 2) {
      items.push({ reason: "pair", points: group.length * (group.length - 1), cards: group });
    }
  }
  return items;
}
function scoreRuns(cards) {
  const byRank = /* @__PURE__ */ new Map();
  for (const card of sortCards(cards)) {
    const rank = rankOf(card);
    const group = byRank.get(rank);
    if (group) group.push(card);
    else byRank.set(rank, [card]);
  }
  const ranks = [...byRank.keys()].sort((a, b) => a - b);
  const items = [];
  let start = 0;
  while (start < ranks.length) {
    let end = start;
    while (end + 1 < ranks.length && must(ranks[end + 1]) === must(ranks[end]) + 1) end++;
    const length = end - start + 1;
    if (length >= 3) {
      const groups = ranks.slice(start, end + 1).map((rank) => must(byRank.get(rank)));
      for (const combo of cartesian(groups)) {
        items.push({ reason: "run", points: length, cards: combo });
      }
    }
    start = end + 1;
  }
  return items;
}
function cartesian(groups) {
  let acc = [[]];
  for (const group of groups) {
    const next = [];
    for (const prefix of acc) {
      for (const card of group) next.push([...prefix, card]);
    }
    acc = next;
  }
  return acc;
}
function allSameSuit(cards) {
  const suit = suitOf(must(cards[0], "flush check needs at least one card"));
  return cards.every((card) => suitOf(card) === suit);
}
function hisHeels(cut) {
  return rankOf(cut) === JACK ? { reason: "his_heels", points: 2, cards: [cut] } : null;
}
function scoreHand(handCards, starter, isCrib = false) {
  invariant(handCards.length === 4, `a show hand is four cards, got ${handCards.length}`);
  const all = starter === null ? [...handCards] : [...handCards, starter];
  const items = [
    ...scoreFifteens(all),
    ...scorePairs(all),
    ...scoreRuns(all),
    ...scoreFlush(handCards, starter, isCrib)
  ];
  if (starter !== null) {
    const starterSuit = suitOf(starter);
    for (const card of sortCards(handCards)) {
      if (rankOf(card) === JACK && suitOf(card) === starterSuit) {
        items.push({ reason: "nobs", points: 1, cards: [card, starter] });
      }
    }
  }
  return items;
}
function scoreFlush(handCards, starter, isCrib) {
  if (!allSameSuit(handCards)) return [];
  const starterMatches = starter !== null && suitOf(starter) === suitOf(must(handCards[0]));
  if (isCrib) {
    return starterMatches ? [{ reason: "flush", points: 5, cards: [...sortCards(handCards), must(starter)] }] : [];
  }
  return starterMatches ? [{ reason: "flush", points: 5, cards: [...sortCards(handCards), must(starter)] }] : [{ reason: "flush", points: 4, cards: sortCards(handCards) }];
}
function scoreFiveCardLine(cards, cutCard = null) {
  invariant(cards.length === 5, `a grid line is five cards, got ${cards.length}`);
  const items = [
    ...scoreFifteens(cards),
    ...scorePairs(cards),
    ...scoreRuns(cards),
    ...scoreLineFlush(cards)
  ];
  if (cutCard !== null) {
    invariant(cards.includes(cutCard), "the cut card is a member of the centre line it is scoring");
    const suit = suitOf(cutCard);
    for (const card of sortCards(cards)) {
      if (card !== cutCard && rankOf(card) === JACK && suitOf(card) === suit) {
        items.push({ reason: "nobs", points: 1, cards: [card, cutCard] });
      }
    }
  }
  return items;
}
function scoreLineFlush(cards) {
  const bySuit = /* @__PURE__ */ new Map();
  for (const card of sortCards(cards)) {
    const suit = suitOf(card);
    const group = bySuit.get(suit);
    if (group) group.push(card);
    else bySuit.set(suit, [card]);
  }
  for (const group of bySuit.values()) {
    if (group.length >= 4) {
      return [{ reason: "flush", points: group.length, cards: group }];
    }
  }
  return [];
}
function scorePegPlay(stack, played) {
  const sequence = [...stack, played];
  const count = totalValue(sequence);
  invariant(count <= 31, `pegging count ${count} exceeds 31`);
  const items = [];
  if (count === 15) items.push({ reason: "fifteen", points: 2, cards: [...sequence] });
  if (count === 31) items.push({ reason: "thirty_one", points: 2, cards: [...sequence] });
  let matching = 1;
  for (let i = stack.length - 1; i >= 0; i--) {
    if (rankOf(must(stack[i])) === rankOf(played)) matching++;
    else break;
  }
  if (matching >= 2) {
    items.push({
      reason: "pair",
      points: matching * (matching - 1),
      cards: sequence.slice(sequence.length - matching)
    });
  }
  for (let length = sequence.length; length >= 3; length--) {
    const tail = sequence.slice(sequence.length - length);
    if (isConsecutiveSet(tail)) {
      items.push({ reason: "run", points: length, cards: tail });
      break;
    }
  }
  return items;
}
function isConsecutiveSet(cards) {
  const ranks = /* @__PURE__ */ new Set();
  let min = Infinity;
  let max = -Infinity;
  for (const card of cards) {
    const rank = rankOf(card);
    if (ranks.has(rank)) return false;
    ranks.add(rank);
    if (rank < min) min = rank;
    if (rank > max) max = rank;
  }
  return max - min === cards.length - 1;
}
function canPlayCard(count, card) {
  return count + valueOf(card) <= 31;
}

// src/cross.ts
var GRID_SIZE = 5;
var GRID_CELLS = GRID_SIZE * GRID_SIZE;
var CENTRE_CELL = 12;
var ROW_TEAM = 0;
var COLUMN_TEAM = 1;
function rowCells(index) {
  return Array.from({ length: GRID_SIZE }, (_, i) => index * GRID_SIZE + i);
}
function columnCells(index) {
  return Array.from({ length: GRID_SIZE }, (_, i) => i * GRID_SIZE + index);
}
function rowOf(cell) {
  return Math.floor(cell / GRID_SIZE);
}
function columnOf(cell) {
  return cell % GRID_SIZE;
}
function setupGrid(ctx) {
  const d = ctx.draft;
  const { items: deck, state: rng } = shuffle(FULL_DECK, d.rng);
  d.rng = rng;
  const board = Array.from({ length: GRID_CELLS }, () => null);
  const centre = must(deck[0]);
  board[CENTRE_CELL] = centre;
  d.deck = deck.slice(1);
  d.cross = {
    board,
    turn: placerOf(d, 0),
    drawn: null,
    gridNumber: d.handNumber,
    throws: Array.from({ length: d.config.playerCount }, () => 0)
  };
  d.phase = "placing";
  d.hands = Array.from({ length: d.config.playerCount }, () => []);
  d.showHands = Array.from({ length: d.config.playerCount }, () => []);
  d.crib = [];
  d.discards = Array.from({ length: d.config.playerCount }, () => []);
  d.starter = null;
  d.discarded = Array.from({ length: d.config.playerCount }, () => false);
  d.pegging = null;
  emit(ctx, { type: "HAND_STARTED", handNumber: d.handNumber, dealer: d.dealer });
  emit(ctx, { type: "CARD_PLACED", seat: null, cell: CENTRE_CELL, card: centre });
  emit(ctx, { type: "PHASE_CHANGED", phase: "placing" });
  for (let seat = 0; seat < d.config.playerCount; seat++) {
    statsFor(ctx, seat).hands_played += 1;
  }
}
function placerOf(state, placed) {
  const cycle = turnCycle(state.config);
  const first = dealerPlace(state.config, state.dealer, state.handNumber) + 1;
  return must(cycle[(first + placed) % cycle.length]);
}
function placedThisGrid(board) {
  return board.filter((cell) => cell !== null).length - 1;
}
function drawRoles(ctx) {
  const d = ctx.draft;
  for (; ; ) {
    const { items: deck, state: rng } = shuffle(FULL_DECK, d.rng);
    d.rng = rng;
    const drawn = deck.slice(0, d.config.playerCount);
    const ranks = drawn.map(rankOf);
    const lowest = Math.min(...ranks);
    if (ranks.filter((rank) => rank === lowest).length > 1) continue;
    const low = must(drawn[ranks.indexOf(lowest)]);
    const rest = drawn.filter((card) => card !== low);
    const cuts = [
      { seat: 0, card: must(rest[0]) },
      { seat: ALONE_SEAT, card: low },
      { seat: 2, card: must(rest[1]) }
    ];
    d.roleDraw = cuts;
    emit(ctx, { type: "ROLES_DRAWN", cuts, alone: ALONE_SEAT });
    return;
  }
}
function throwQuota(config, seat) {
  if (isTeamAgainstOne(config)) return seat === ALONE_SEAT ? 2 : 1;
  return discardCount(config);
}
function cribSize(config) {
  let size = 0;
  for (let seat = 0; seat < config.playerCount; seat++) size += throwQuota(config, seat);
  return size;
}
function throwsBySeat(cross, seat) {
  return cross?.throws[seat] ?? 0;
}
function owesThrow(state, seat) {
  return state.config.crib && throwsBySeat(state.cross, seat) < throwQuota(state.config, seat);
}
function nextThrower(d) {
  const placed = placedThisGrid(must(d.cross).board);
  const length = turnCycle(d.config).length;
  for (let step = 0; step < length; step++) {
    const seat = placerOf(d, placed + step);
    if (owesThrow(d, seat)) return seat;
  }
  return null;
}
function gridFinished(d) {
  const cross = must(d.cross);
  if (!cross.board.every((cell) => cell !== null)) return false;
  if (!d.config.crib) return true;
  return d.crib.length === cribSize(d.config);
}
function legalCrossMoves(state, seat) {
  if (state.phase !== "placing") return [];
  const cross = state.cross;
  if (cross?.drawn == null || cross.turn !== seat) return [];
  const moves = cross.board.flatMap(
    (card, cell) => card === null ? [{ type: "place", cell }] : []
  );
  if (owesThrow(state, seat)) {
    moves.push({ type: "discard", cards: [cross.drawn] });
  }
  return moves;
}
function awaitingCrossSeats(state) {
  if (state.phase !== "placing") return [];
  const cross = state.cross;
  if (cross?.drawn == null) return [];
  return [cross.turn];
}
function applyCrossMove(ctx, seat, move) {
  const d = ctx.draft;
  const throwing = move.type === "discard" && d.config.crib;
  if (move.type !== "place" && !throwing) {
    illegal("WRONG_PHASE", "Cross-Cribbage only accepts placements");
  }
  if (d.phase !== "placing") illegal("WRONG_PHASE");
  const cross = must(d.cross);
  if (cross.turn !== seat) illegal("NOT_YOUR_TURN");
  const card = cross.drawn;
  if (card === null) illegal("WRONG_PHASE", "no card has been turned yet");
  if (move.type === "discard") {
    applyThrow(ctx, seat, move.cards, card);
    return;
  }
  if (!Number.isInteger(move.cell) || move.cell < 0 || move.cell >= GRID_CELLS) {
    illegal("BAD_CELL");
  }
  if (cross.board[move.cell] !== null) illegal("CELL_OCCUPIED");
  cross.board[move.cell] = card;
  cross.drawn = null;
  emit(ctx, { type: "CARD_PLACED", seat, cell: move.cell, card });
  cross.turn = placerOf(d, placedThisGrid(cross.board));
}
function applyThrow(ctx, seat, cards, drawn) {
  const d = ctx.draft;
  const cross = must(d.cross);
  if (cards.length !== 1) illegal("WRONG_DISCARD_COUNT", "throw exactly one card");
  if (cards[0] !== drawn) illegal("CARD_NOT_HELD", "you can only throw the card you turned");
  if (!owesThrow(d, seat)) {
    illegal("ALREADY_DISCARDED", "this seat has sent its share to the crib");
  }
  d.crib.push(drawn);
  must(d.discards[seat]).push(drawn);
  cross.throws[seat] = must(cross.throws[seat]) + 1;
  cross.drawn = null;
  emit(ctx, { type: "DISCARDED", seat, cards: [drawn], count: 1 });
}
function stepCross(ctx) {
  const d = ctx.draft;
  if (d.phase === "placing") {
    const cross = must(d.cross);
    if (gridFinished(d)) {
      d.phase = "gridScoring";
      emit(ctx, { type: "PHASE_CHANGED", phase: "gridScoring" });
      return false;
    }
    if (d.config.crib && cross.board.every((cell) => cell !== null)) {
      cross.turn = must(nextThrower(d));
    }
    if (cross.drawn === null) {
      const card = must(d.deck.shift());
      cross.drawn = card;
      emit(ctx, { type: "CARD_DRAWN", seat: cross.turn, card });
    }
    return true;
  }
  if (d.phase === "gridScoring") {
    scoreGrid(ctx);
    return false;
  }
  return true;
}
function scoreGrid(ctx) {
  const d = ctx.draft;
  const cross = must(d.cross);
  const rowLines = Array.from(
    { length: GRID_SIZE },
    (_, index) => ({
      line: { kind: "row", index },
      team: ROW_TEAM,
      cells: rowCells(index)
    })
  );
  const columnLines = Array.from(
    { length: GRID_SIZE },
    (_, index) => ({
      line: { kind: "column", index },
      team: COLUMN_TEAM,
      cells: columnCells(index)
    })
  );
  const dealingTeam = teamOf(d.config, d.dealer);
  const lines = dealingTeam === ROW_TEAM ? [...columnLines, ...rowLines] : [...rowLines, ...columnLines];
  const totals = Array.from({ length: teamCount(d.config) }, () => 0);
  const cutCard = must(cross.board[CENTRE_CELL]);
  const centre = (GRID_SIZE - 1) / 2;
  for (const { line, team, cells } of lines) {
    const cards = cells.map((cell) => must(cross.board[cell]));
    const holdsCutCard = line.index === centre;
    const items = scoreFiveCardLine(cards, holdsCutCard ? cutCard : null);
    const total = totalPoints(items);
    emit(ctx, { type: "LINE_COMPLETED", line, team, cells, items, total });
    totals[team] = must(totals[team]) + total;
    for (const member of seatsOfTeam(d.config, team)) {
      const stats = statsFor(ctx, member);
      if (total > stats.best_grid_line) stats.best_grid_line = total;
    }
  }
  let hisHeels2 = null;
  const item = hisHeels(cutCard);
  if (item !== null) {
    const team = teamOf(d.config, d.dealer);
    totals[team] = must(totals[team]) + item.points;
    hisHeels2 = { team, item };
  }
  let cribScore = null;
  if (d.config.crib) {
    const team = teamOf(d.config, d.dealer);
    const items = scoreHand(d.crib, cutCard, true);
    const total = totalPoints(items);
    totals[team] = must(totals[team]) + total;
    cribScore = { cards: [...d.crib], items, total, team };
  }
  const rows = must(totals[ROW_TEAM]);
  const columns = must(totals[COLUMN_TEAM]);
  const margin = Math.abs(rows - columns);
  const winner = margin === 0 ? null : rows > columns ? ROW_TEAM : COLUMN_TEAM;
  emit(ctx, {
    type: "GRID_COMPLETED",
    gridNumber: cross.gridNumber,
    totals,
    hisHeels: hisHeels2,
    margin,
    winner,
    /* A copy, not the array: `setupGrid` is about to write the next grid's
       cards into this one, and an event holding the live reference would
       silently become a picture of the wrong grid. */
    board: [...cross.board],
    crib: cribScore
  });
  if (winner !== null) {
    const item2 = { reason: "margin", points: margin, cards: [] };
    award(ctx, creditSeatOf(d.config, winner), [item2], "grid", "team");
    if (d.winner !== null) return;
  }
  const cycle = turnCycle(d.config);
  d.dealer = must(cycle[(dealerPlace(d.config, d.dealer, d.handNumber) + 1) % cycle.length]);
  d.handNumber += 1;
  setupGrid(ctx);
}
function creditSeatOf(config, team) {
  return must(seatsOfTeam(config, team)[0]);
}

// src/redact.ts
function isSeat(viewer) {
  return typeof viewer === "number";
}
function redactStateFor(state, viewer, awaitingSeats2) {
  const seat = isSeat(viewer) ? viewer : null;
  const opponents = [];
  for (let s = 0; s < state.config.playerCount; s++) {
    if (s === seat) continue;
    opponents.push({
      seat: s,
      team: teamOf(state.config, s),
      handCount: state.hands[s]?.length ?? 0,
      discarded: state.discarded[s] ?? false
    });
  }
  return {
    config: state.config,
    engineVersion: state.engineVersion,
    viewer,
    seq: state.seq,
    phase: state.phase,
    handNumber: state.handNumber,
    dealer: state.dealer,
    scores: [...state.scores],
    prevScores: [...state.prevScores],
    deckCount: state.deck.length,
    hand: seat === null ? [] : [...state.hands[seat] ?? []],
    opponents,
    cribCount: state.crib.length,
    ownDiscards: seat === null ? [] : [...state.discards[seat] ?? []],
    // The crib is face down until the show, and the show never rests — so the
    // only state in which the crib is public is the finished game.
    crib: state.phase === "gameOver" ? [...state.crib] : null,
    starter: state.starter,
    pegging: state.pegging === null ? null : {
      count: state.pegging.count,
      stack: [...state.pegging.stack],
      history: state.pegging.history.map((p) => ({ ...p })),
      turn: state.pegging.turn,
      saidGo: [...state.pegging.saidGo],
      lastPlayer: state.pegging.lastPlayer
    },
    // Cross-Cribbage has no hidden information: every placed card and the card
    // currently turned are visible to the whole table.
    //
    // The dealer's crib is the one exception. Burying a card only means
    // anything when nobody has seen it, so with the option on the turned card
    // is private to the seat holding it, spectators included, until it is
    // placed or thrown. Everything else about the grid stays public, `throws`
    // included: a quota that has been spent is a fact both sides get to count.
    cross: state.cross === null ? null : {
      board: [...state.cross.board],
      turn: state.cross.turn,
      drawn: state.config.crib && viewer !== state.cross.turn ? null : state.cross.drawn,
      gridNumber: state.cross.gridNumber,
      throws: [...state.cross.throws]
    },
    roleDraw: copyRoleDraw(state.roleDraw),
    awaitingSeats: [...awaitingSeats2],
    winner: state.winner,
    result: state.result
  };
}
function redactEventsFor(events, viewer, config) {
  const seat = isSeat(viewer) ? viewer : null;
  return events.map((event) => {
    switch (event.type) {
      case "CARDS_DEALT":
        return event.seat === seat ? event : { ...event, cards: [] };
      case "DISCARDED":
        return event.seat === seat ? event : { ...event, cards: null };
      case "CRIB_SEEDED":
        return { ...event, card: null };
      case "CARD_DRAWN":
        return !config.crib || event.seat === seat ? event : { ...event, card: null };
      default:
        return event;
    }
  });
}

// src/standard.ts
function cutForDeal(ctx) {
  const d = ctx.draft;
  const drawers = dealDrawers(d.config);
  for (; ; ) {
    const { items: deck, state: rng } = shuffle(FULL_DECK, d.rng);
    d.rng = rng;
    const cuts = drawers.map((seat, index) => ({ seat, card: must(deck[index]) }));
    let lowest = Infinity;
    let dealer = 0;
    let tied = false;
    for (const cut of cuts) {
      const rank = rankOf(cut.card);
      if (rank < lowest) {
        lowest = rank;
        dealer = cut.seat;
        tied = false;
      } else if (rank === lowest) {
        tied = true;
      }
    }
    if (tied) continue;
    d.dealer = dealer;
    emit(ctx, { type: "CUT_FOR_DEAL", cuts, dealer });
    return;
  }
}
function dealHand(ctx) {
  const d = ctx.draft;
  const { config } = d;
  const perPlayer = cardsPerPlayer(config);
  const { items: deck, state: rng } = shuffle(FULL_DECK, d.rng);
  d.rng = rng;
  d.hands = Array.from({ length: config.playerCount }, () => []);
  d.showHands = Array.from({ length: config.playerCount }, () => []);
  d.crib = [];
  d.discards = Array.from({ length: config.playerCount }, () => []);
  d.starter = null;
  d.pegging = null;
  d.discarded = Array.from({ length: config.playerCount }, () => false);
  d.phase = "discard";
  emit(ctx, { type: "HAND_STARTED", handNumber: d.handNumber, dealer: d.dealer });
  let cursor = 0;
  for (let round = 0; round < perPlayer; round++) {
    for (const seat of seatOrderFrom(config, nextSeat(config, d.dealer))) {
      must(d.hands[seat]).push(must(deck[cursor]));
      cursor += 1;
    }
  }
  for (const seat of seatOrderFrom(config, nextSeat(config, d.dealer))) {
    statsFor(ctx, seat).hands_played += 1;
    emit(ctx, {
      type: "CARDS_DEALT",
      seat,
      cards: [...must(d.hands[seat])],
      count: perPlayer
    });
  }
  if (config.playerCount === 3) {
    const seeded = must(deck[cursor]);
    cursor += 1;
    d.crib.push(seeded);
    emit(ctx, { type: "CRIB_SEEDED", card: seeded });
  }
  d.deck = deck.slice(cursor);
  emit(ctx, { type: "PHASE_CHANGED", phase: "discard" });
}
function legalStandardMoves(state, seat) {
  if (seat < 0 || seat >= state.config.playerCount) return [];
  switch (state.phase) {
    case "discard": {
      if (state.discarded[seat] === true) return [];
      const held = state.hands[seat];
      if (held === void 0) return [];
      return choose(held, discardCount(state.config)).map((cards) => ({
        type: "discard",
        cards
      }));
    }
    case "pegging": {
      const peg = state.pegging;
      if (peg?.turn !== seat) return [];
      const held = state.hands[seat] ?? [];
      return held.filter((card) => canPlayCard(peg.count, card)).map((card) => ({ type: "play", card }));
    }
    default:
      return [];
  }
}
function awaitingStandardSeats(state) {
  switch (state.phase) {
    case "discard":
      return state.discarded.flatMap((done, seat) => done ? [] : [seat]);
    case "pegging":
      return state.pegging === null ? [] : [state.pegging.turn];
    default:
      return [];
  }
}
function applyStandardMove(ctx, seat, move) {
  const d = ctx.draft;
  if (seat < 0 || seat >= d.config.playerCount) illegal("UNKNOWN_SEAT");
  switch (move.type) {
    case "discard": {
      if (d.phase !== "discard") illegal("WRONG_PHASE", "not the discard phase");
      if (d.discarded[seat] === true) illegal("ALREADY_DISCARDED");
      const required = discardCount(d.config);
      if (move.cards.length !== required) {
        illegal("WRONG_DISCARD_COUNT", `discard exactly ${required}`);
      }
      const held = must(d.hands[seat]);
      const seen = /* @__PURE__ */ new Set();
      for (const card of move.cards) {
        if (seen.has(card)) illegal("DUPLICATE_CARD");
        seen.add(card);
        if (!held.includes(card)) illegal("CARD_NOT_HELD");
      }
      const kept = held.filter((card) => !seen.has(card));
      d.hands[seat] = kept;
      d.showHands[seat] = [...kept];
      d.crib.push(...move.cards);
      d.discards[seat] = [...move.cards];
      d.discarded[seat] = true;
      emit(ctx, {
        type: "DISCARDED",
        seat,
        cards: [...move.cards],
        count: move.cards.length
      });
      return;
    }
    case "play": {
      if (d.phase !== "pegging") illegal("WRONG_PHASE", "not the pegging phase");
      const peg = must(d.pegging);
      if (peg.turn !== seat) illegal("NOT_YOUR_TURN");
      const held = must(d.hands[seat]);
      if (!held.includes(move.card)) illegal("CARD_NOT_HELD");
      if (!canPlayCard(peg.count, move.card)) illegal("EXCEEDS_31");
      const stackBefore = [...peg.stack];
      d.hands[seat] = held.filter((card) => card !== move.card);
      peg.stack.push(move.card);
      peg.history.push({ seat, card: move.card });
      peg.count += valueOf(move.card);
      peg.lastPlayer = seat;
      emit(ctx, { type: "CARD_PLAYED", seat, card: move.card, count: peg.count });
      award(ctx, seat, scorePegPlay(stackBefore, move.card), "pegging");
      peg.turn = nextSeat(d.config, seat);
      return;
    }
    case "place":
      return illegal("WRONG_PHASE", "placement is a Cross-Cribbage move");
    default:
      return illegal("UNKNOWN_MOVE");
  }
}
function stepStandard(ctx) {
  const d = ctx.draft;
  switch (d.phase) {
    case "discard":
      if (d.discarded.some((done) => !done)) return true;
      completeDiscards(ctx);
      return false;
    case "pegging": {
      const actor = stepPegging(ctx);
      if (actor === null) return false;
      must(d.pegging).turn = actor;
      return true;
    }
    case "show":
      runShow(ctx);
      return false;
    default:
      return true;
  }
}
function completeDiscards(ctx) {
  const d = ctx.draft;
  emit(ctx, { type: "ALL_DISCARDED" });
  invariant(d.crib.length === 4, `crib should hold four cards, has ${d.crib.length}`);
  const starter = must(d.deck.shift());
  d.starter = starter;
  emit(ctx, { type: "STARTER_CUT", card: starter, dealer: d.dealer });
  const heels = hisHeels(starter);
  if (heels !== null) {
    award(ctx, d.dealer, [heels], "heels");
    if (d.winner !== null) return;
  }
  d.phase = "pegging";
  d.pegging = {
    count: 0,
    stack: [],
    history: [],
    turn: nextSeat(d.config, d.dealer),
    saidGo: Array.from({ length: d.config.playerCount }, () => false),
    lastPlayer: null
  };
  emit(ctx, { type: "PHASE_CHANGED", phase: "pegging" });
}
function stepPegging(ctx) {
  const d = ctx.draft;
  const peg = must(d.pegging);
  for (; ; ) {
    const actor = nextPeggingActor(ctx);
    if (actor !== null) return actor;
    const everyoneEmpty = d.hands.every((held) => held.length === 0);
    if (peg.count > 0 && peg.count !== 31 && peg.lastPlayer !== null) {
      const lastCard = must(peg.stack[peg.stack.length - 1]);
      award(
        ctx,
        peg.lastPlayer,
        [{ reason: everyoneEmpty ? "last_card" : "go", points: 1, cards: [lastCard] }],
        "pegging"
      );
      if (d.winner !== null) return null;
    }
    if (everyoneEmpty) {
      emit(ctx, { type: "PEGGING_ENDED" });
      d.phase = "show";
      emit(ctx, { type: "PHASE_CHANGED", phase: "show" });
      return null;
    }
    peg.count = 0;
    peg.stack = [];
    peg.saidGo = Array.from({ length: d.config.playerCount }, () => false);
    emit(ctx, { type: "COUNT_RESET" });
    peg.turn = nextSeatWithCards(ctx, nextSeat(d.config, peg.lastPlayer ?? peg.turn));
  }
}
function nextPeggingActor(ctx) {
  const d = ctx.draft;
  const peg = must(d.pegging);
  if (peg.count === 31) return null;
  for (const seat of seatOrderFrom(d.config, peg.turn)) {
    const held = must(d.hands[seat]);
    if (held.length === 0) continue;
    if (peg.saidGo[seat] === true) continue;
    if (held.some((card) => canPlayCard(peg.count, card))) return seat;
    peg.saidGo[seat] = true;
    emit(ctx, { type: "GO_DECLARED", seat });
  }
  return null;
}
function nextSeatWithCards(ctx, from) {
  const d = ctx.draft;
  for (const seat of seatOrderFrom(d.config, from)) {
    if (must(d.hands[seat]).length > 0) return seat;
  }
  return from;
}
function runShow(ctx) {
  const d = ctx.draft;
  const starter = must(d.starter);
  for (const seat of seatOrderFrom(d.config, nextSeat(d.config, d.dealer))) {
    const cards = must(d.showHands[seat]);
    const items = scoreHand(cards, starter, false);
    const total = totalPoints(items);
    emit(ctx, { type: "HAND_SHOWN", seat, cards: [...cards], starter, items, total });
    const stats = statsFor(ctx, seat);
    if (total > stats.highest_hand) stats.highest_hand = total;
    if (total === 29) stats.twenty_nine_hands += 1;
    award(ctx, seat, items, "hand");
    if (d.winner !== null) return;
  }
  const cribItems = scoreHand(d.crib, starter, true);
  const cribTotal = totalPoints(cribItems);
  emit(ctx, {
    type: "CRIB_SHOWN",
    seat: d.dealer,
    cards: [...d.crib],
    starter,
    items: cribItems,
    total: cribTotal
  });
  award(ctx, d.dealer, cribItems, "crib");
  if (d.winner !== null) return;
  d.handNumber += 1;
  d.dealer = nextSeat(d.config, d.dealer);
  dealHand(ctx);
}

// src/types.ts
var ENGINE_VERSION = "1.1.0";
function supportsReplayVersion(variant, version = "1.0.0") {
  return version === ENGINE_VERSION || variant === "standard" && version === "1.0.0";
}
var CROSS_TARGET_SCORE = 31;

// src/game.ts
function normalizeConfig(input = {}) {
  const variant = input.variant ?? "standard";
  const playerCount = input.playerCount ?? 2;
  const teams = input.teams ?? playerCount === 4;
  const crib = input.crib ?? false;
  const targetScore = variant === "cross" ? CROSS_TARGET_SCORE : input.targetScore ?? 121;
  invariant(
    playerCount === 2 || playerCount === 3 || playerCount === 4,
    `unsupported player count ${playerCount}`
  );
  invariant(
    variant === "cross" || targetScore === 121 || targetScore === 61,
    `unsupported target ${targetScore}`
  );
  invariant(playerCount === 4 || !teams, "teams are a four-player format");
  invariant(
    variant !== "cross" || playerCount !== 4 || teams,
    "four-player Cross-Cribbage is partners only"
  );
  invariant(!crib || variant === "cross", "the crib option belongs to Cross-Cribbage");
  return { variant, playerCount, teams, targetScore, crib };
}
function createGame(input, seed) {
  const config = normalizeConfig(input);
  const draft = {
    config,
    engineVersion: ENGINE_VERSION,
    rng: seedRng(seed),
    seq: 0,
    phase: "cutForDeal",
    handNumber: 1,
    dealer: 0,
    scores: Array.from({ length: teamCount(config) }, () => 0),
    prevScores: Array.from({ length: teamCount(config) }, () => 0),
    deck: [],
    hands: Array.from({ length: config.playerCount }, () => []),
    showHands: Array.from({ length: config.playerCount }, () => []),
    crib: [],
    discards: Array.from({ length: config.playerCount }, () => []),
    starter: null,
    discarded: Array.from({ length: config.playerCount }, () => false),
    pegging: null,
    cross: null,
    roleDraw: null,
    stats: Array.from({ length: config.playerCount }, () => emptyStats()),
    winner: null,
    result: null
  };
  const ctx = { draft, events: [] };
  emit(ctx, { type: "GAME_STARTED", config });
  if (isTeamAgainstOne(config)) drawRoles(ctx);
  cutForDeal(ctx);
  if (config.variant === "cross") {
    setupGrid(ctx);
  } else {
    dealHand(ctx);
  }
  runCascade(ctx);
  return { state: commit(draft), events: ctx.events };
}
function legalMoves(state, seat) {
  if (state.phase === "gameOver") return [];
  return state.config.variant === "cross" ? legalCrossMoves(state, seat) : legalStandardMoves(state, seat);
}
function awaitingSeats(state) {
  if (state.phase === "gameOver") return [];
  return state.config.variant === "cross" ? awaitingCrossSeats(state) : awaitingStandardSeats(state);
}
function applyMove(state, seat, move) {
  if (state.phase === "gameOver") illegal("GAME_OVER");
  const draft = draftOf(state);
  const ctx = { draft, events: [] };
  if (move.type === "forfeit") {
    applyForfeit(ctx, seat);
    return { state: commit(draft), events: ctx.events };
  }
  if (draft.config.variant === "cross") {
    applyCrossMove(ctx, seat, move);
  } else {
    applyStandardMove(ctx, seat, move);
  }
  runCascade(ctx);
  return { state: commit(draft), events: ctx.events };
}
function redactFor(state, viewer) {
  return redactStateFor(state, viewer, awaitingSeats(state));
}
function applyForfeit(ctx, seat) {
  const d = ctx.draft;
  if (!Number.isInteger(seat) || seat < 0 || seat >= d.config.playerCount) {
    illegal("UNKNOWN_SEAT");
  }
  const forfeiting = teamOf(d.config, seat);
  const rivals = Array.from({ length: teamCount(d.config) }, (_, team) => team).filter(
    (team) => team !== forfeiting
  );
  d.winner = rivals.reduce(
    (best, team) => must(d.scores[team]) > must(d.scores[best]) ? team : best
  );
  finalize(ctx, seat);
}
function runCascade(ctx) {
  const isCross = ctx.draft.config.variant === "cross";
  for (let guard = 0; guard < 1e5; guard++) {
    if (ctx.draft.winner !== null) {
      finalize(ctx);
      return;
    }
    const resting = isCross ? stepCross(ctx) : stepStandard(ctx);
    if (resting) return;
  }
  invariant(false, "engine cascade failed to settle");
}

export { ALONE_SEAT, CENTRE_CELL, COLUMN_TEAM, CROSS_DOUBLE_SKUNK_LINE, CROSS_SKUNK_LINE, CROSS_TARGET_SCORE, DECK_SIZE, ENGINE_VERSION, EngineInvariantError, FULL_DECK, GRID_CELLS, GRID_SIZE, IllegalMoveError, JACK, RANK_COUNT, RANK_LETTERS, RANK_PIPS, ROW_TEAM, SUIT_COUNT, SUIT_LETTERS, SUIT_SYMBOLS, applyMove, awaitingSeats, canPlayCard, cardCode, cardLabel, cardsPerPlayer, choose, columnCells, columnOf, combinations, compareCards, createGame, cribSize, dealDrawers, dealerPlace, discardCount, doubleSkunkLine, formatCards, hand, hashSeed, hisHeels, isCard, isPartners, isTeamAgainstOne, legalMoves, makeCard, nextFloat, nextInt, nextSeat, nextUint32, normalizeConfig, owesThrow, parseCard, pick, placedThisGrid, placerOf, rankOf, redactEventsFor, redactFor, rowCells, rowOf, scoreFifteens, scoreFiveCardLine, scoreHand, scorePairs, scorePegPlay, scoreRuns, seatOrderFrom, seatsOfTeam, seedRng, shuffle, skunkLine, sortCards, suitOf, supportsReplayVersion, teamCount, teamOf, throwQuota, throwsBySeat, totalPoints, totalValue, turnCycle, valueOf };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map